import { useState } from 'react';
import { Alert, AlertTitle, Dialog, DialogTitle, DialogActions, IconButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import CombinationRuleForm from './CombinationRuleForm';
import { TimekeeperService } from 'services/TimekeeperService';

const tk = new TimekeeperService();
interface OvertimeRule {
    overtime_rule_id: number;
    name: string;
}

interface Zone {
    zone_id: number;
    name: string;
}

interface ScheduleDesviationRule {
    schedule_desviation_rule_id: number;
    name: string;
}

interface CombinationRuleFormData {
    combination_rule_id: number | string;
    name: string;
    description: string;
    overtimeSelect: number[];
    zoneSelect: number[];
    scheduleDesviationSelect: number[];
    overtime_rules: OvertimeRule[];
    zones: Zone[];
    schedule_desviation_rules: ScheduleDesviationRule[];
}

interface CombinationRuleRow {
    id: number;
    name: string;
    description?: string;
    overtime_rules?: OvertimeRule[];
    zones?: Zone[];
    schedule_desviation_rules?: ScheduleDesviationRule[];
}

interface DuplicateCombinationsProps {
    open: boolean;
    setOpenDuplicate: (open: boolean) => void;
    rows: CombinationRuleRow[];
    select: CombinationRuleRow | null;
    reloading: () => void;
}

const DuplicateCombinations = ({ open, setOpenDuplicate, rows, select, reloading }: DuplicateCombinationsProps) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(false);
    const [alert, setAlert] = useState<boolean>(false);

    const [formData, setFormData] = useState<CombinationRuleFormData>({
        combination_rule_id: select?.id || '',
        name: '',
        description: '',
        overtimeSelect: [],
        zoneSelect: [],
        scheduleDesviationSelect: [],
        overtime_rules: [],
        zones: [],
        schedule_desviation_rules: [],
    });

    const cleanForm = (): void => {
        setFormData({
            combination_rule_id: '',
            name: '',
            description: '',
            overtimeSelect: [],
            zoneSelect: [],
            scheduleDesviationSelect: [],
            overtime_rules: [],
            zones: [],
            schedule_desviation_rules: [],
        });
        setOpenDuplicate(false);
        setAlert(false);
    };

    const existName = (name: string): boolean => {
        if (!name || name.trim() === '') return false;
        return rows.some((row) => String(row.name).toLowerCase() === String(name).toLowerCase().trim());
    };

    const changes = (): void => {
        const hasChanges =
            formData.name !== '' ||
            formData.description !== '' ||
            formData.overtimeSelect.length > 0 ||
            formData.zoneSelect.length > 0 ||
            formData.scheduleDesviationSelect.length > 0;
        hasChanges ? setAlert(true) : cleanForm();
    };

    const disabled = (): boolean => {
        if (!formData.name || formData.name.trim() === '' || existName(formData.name)) return true;

        const selectionCount = [formData.overtimeSelect, formData.zoneSelect, formData.scheduleDesviationSelect].filter(
            (array) => array.length > 0,
        ).length;

        return selectionCount < 1;
    };

    const duplicateCombinationRule = async (): Promise<void> => {
        setLoading(true);
        try {
            const data = {
                name: formData.name,
                description: formData.description,
                payment_method: true,
                zones: formData.zones.map((zone) => zone.zone_id),
                overtime_rules: formData.overtime_rules.map((overtime) => overtime.overtime_rule_id),
                schedule_desviation_rules: formData.schedule_desviation_rules.map(
                    (schedule_desviation_rule) => schedule_desviation_rule.schedule_desviation_rule_id,
                ),
            };
            await tk.combination_rule.createCombination(data);

            enqueueSnackbar(t('settings.combinations.dialog.alerts.duplicate.success'), {
                variant: 'success',
            });
            cleanForm();
            reloading();
        } catch (error) {
            console.error('Error duplicating combination:', error);
            enqueueSnackbar(t('settings.combinations.dialog.alerts.duplicate.error'), {
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Dialog
                maxWidth="sm"
                fullWidth
                open={open}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        return;
                    }
                    changes();
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t('settings.combinations.dialog.titles.duplicate')}</DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => changes()}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <Box sx={{ top: 0, zIndex: 10, pb: 1 }}>
                    {alert && (
                        <Alert
                            variant="filled"
                            severity="warning"
                            sx={{ mt: 2 }}
                            action={
                                <>
                                    <Button
                                        onClick={() => setAlert(false)}
                                        color="inherit"
                                        size="small"
                                        variant="text"
                                        disableElevation
                                    >
                                        {t('general.alerts.unsaved_changes.buttons.yes')}
                                    </Button>

                                    <Button
                                        onClick={() => cleanForm()}
                                        color="inherit"
                                        size="small"
                                        variant="text"
                                        disableElevation
                                    >
                                        {t('general.alerts.unsaved_changes.buttons.no')}
                                    </Button>
                                </>
                            }
                        >
                            <AlertTitle>{t('general.alerts.unsaved_changes.message')}</AlertTitle>
                        </Alert>
                    )}
                </Box>
                <CombinationRuleForm
                    mode="duplicate"
                    select={select}
                    rows={rows}
                    formData={formData}
                    setFormData={setFormData}
                />
                <DialogActions>
                    <Button variant="contained" color="error" onClick={() => changes()}>
                        {t('settings.combinations.dialog.buttons.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => duplicateCombinationRule()}
                        disabled={disabled()}
                        loading={loading}
                    >
                        {t('settings.combinations.dialog.buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export { DuplicateCombinations };
